import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import Photo, { Avatar } from "../components/Photo";
import { photoUrl } from "../data/photos";
import Reveal from "../components/Reveal";
import { Arrow } from "../components/Icons";
import { CTABand } from "../components/Shared";
import { getPost, posts } from "../data/posts";

const fmt = (d) => new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "long", day: "numeric" });

function Block({ b }) {
  switch (b.t) {
    case "h2": return <h2>{b.c}</h2>;
    case "h3": return <h3>{b.c}</h3>;
    case "ul": return <ul>{b.c.map((li) => <li key={li}>{li}</li>)}</ul>;
    case "quote": return <blockquote>{b.c}</blockquote>;
    default: return <p>{b.c}</p>;
  }
}

export default function InsightPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const image = post ? photoUrl(post.image) : undefined;

  useSEO({
    title: post ? post.title : "Article not found",
    description: post ? post.excerpt : "",
    path: `/insights/${slug}`,
    type: "article",
    image,
    jsonLd: post && {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: "Victrie", url: "https://victrie.com" },
      publisher: { "@id": "https://victrie.com/#organization" },
      mainEntityOfPage: `https://victrie.com/insights/${post.slug}`,
      keywords: post.tags.join(", "),
    },
  });

  if (!post) return <Navigate to="/insights" replace />;

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <section className="page-head" style={{ paddingBottom: 0 }}>
        <div className="container article">
          <Reveal>
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link><span>/</span><Link to="/insights">Insights</Link><span>/</span><span>{post.category}</span>
            </nav>
            <h1 style={{ fontSize: "clamp(1.8rem, 2.8vw, 2.6rem)" }}>{post.title}</h1>
            <div className="article__meta">
              <span>By Victrie Team</span><span>{fmt(post.date)}</span><span>{post.readTime} min read</span>
            </div>
            <Photo className="article__hero" image={post.image} alt={post.title} />
          </Reveal>
        </div>
      </section>

      <article className="section" style={{ paddingTop: 0 }}>
        <div className="container article">
          <Reveal>
            <p className="lead">{post.excerpt}</p>
            {post.body.map((b, i) => <Block key={i} b={b} />)}
            <div className="article__tags">{post.tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>
            <div className="author-box glass">
              <Avatar name="Victrie" />
              <div>
                <strong>Written by the Victrie Team</strong>
                <p>Engineers, designers and AI researchers building next-generation software in Nairobi, Kenya. <a href="mailto:future@victrie.com" style={{ color: "var(--accent)" }}>future@victrie.com</a></p>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal className="sec-head"><span className="eyebrow">Keep reading</span><h2>Related insights</h2></Reveal>
          <div className="grid grid--3">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80} className="card post-card" as="article">
                <Link to={`/insights/${p.slug}`}><Photo image={p.image} alt={p.title} hover /></Link>
                <div className="post-card__body">
                  <div className="post-card__meta"><b>{p.category}</b><span>{p.readTime} min read</span></div>
                  <h3><Link to={`/insights/${p.slug}`}>{p.title}</Link></h3>
                  <Link to={`/insights/${p.slug}`} className="link-arrow">Read article <Arrow /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
